/* Protect multi-size garment calculators before CPC handles cart submission.
 * Single-quantity merchandise calculators and valid blank garments are unchanged.
 * This is a storefront guard; durable order validation remains a separate task. */
(function () {
  'use strict';

  const sizeName = /^(?:[x]{0,2}small|medium|[x]{0,2}large|[2-7]xlarge|size \d+|\d+-\d+ years):$/i;
  const errorClass = 'oakdene-calculator-error';
  let errorIndex = 0;

  function sizeInputs(calculator) {
    return Array.from(calculator.querySelectorAll('input[type="number"][data-calcname]')).filter(function (input) {
      return sizeName.test(input.getAttribute('data-calcname')) && !input.hasAttribute('data-lookup');
    });
  }

  function colourInput(calculator) {
    return Array.from(calculator.querySelectorAll('textarea, input[type="text"]')).find(function (input) {
      return /chosen.*colou?r/i.test((input.name || '') + ' ' + (input.getAttribute('data-calcname') || ''));
    });
  }

  function normaliseColour(value) {
    return String(value || '').trim().toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '');
  }

  function clearError(calculator) {
    const error = calculator.querySelector('.' + errorClass);
    if (!error) return;
    calculator.querySelectorAll('[data-oakdene-invalid]').forEach(function (input) {
      input.removeAttribute('aria-invalid');
      input.removeAttribute('data-oakdene-invalid');
      const descriptions = (input.getAttribute('aria-describedby') || '').split(/\s+/).filter(function (id) {
        return id && id !== error.id;
      });
      if (descriptions.length) input.setAttribute('aria-describedby', descriptions.join(' '));
      else input.removeAttribute('aria-describedby');
    });
    error.remove();
  }

  function showError(calculator, input, message) {
    clearError(calculator);
    const error = document.createElement('div');
    error.id = 'oakdene-calculator-error-' + (++errorIndex);
    error.className = errorClass;
    error.setAttribute('role', 'alert');
    error.setAttribute('tabindex', '-1');
    error.style.cssText = 'padding:12px;margin:0 0 16px;border:2px solid #003e4f;border-radius:4px;color:#003e4f;background:#fff;font-size:16px;line-height:1.5;';
    error.textContent = message;
    calculator.prepend(error);
    if (input && !input.disabled) {
      input.setAttribute('aria-invalid', 'true');
      input.setAttribute('data-oakdene-invalid', 'true');
      const descriptions = (input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
      descriptions.push(error.id);
      input.setAttribute('aria-describedby', descriptions.join(' '));
      input.focus();
    } else error.focus();
    error.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  function validate(form) {
    const calculator = form.querySelector('#calculator');
    if (!calculator) return true;
    const sizes = sizeInputs(calculator);
    const colour = colourInput(calculator);
    const selectedQuantity = calculator.querySelector('input[type="hidden"][data-cpc_isquantity="true"]');
    // The apparel CPC schema has a computed quantity and many size fields.
    // Bags/notebooks have a directly entered quantity and must keep their MOQ rules.
    if (!selectedQuantity || sizes.length < 2 || !colour) return true;

    let total = 0;
    for (const input of sizes) {
      const raw = input.value.trim();
      const value = raw === '' ? 0 : Number(raw);
      const max = input.max === '' ? Infinity : Number(input.max);
      if (!Number.isSafeInteger(value) || value < 0 || (Number.isFinite(max) && value > max) || input.validity.badInput) {
        showError(calculator, input, 'Enter a whole number of garments from 0' + (Number.isFinite(max) ? ' to ' + max : '') + ' for each size.');
        return false;
      }
      total += value;
    }
    if (!Number.isSafeInteger(total) || total < 1) {
      showError(calculator, sizes.find(function (input) { return !input.disabled && input.getClientRects().length; }), 'Choose at least one garment by entering a quantity for your required size.');
      return false;
    }

    const colourValue = normaliseColour(colour.value);
    if (!colourValue) {
      showError(calculator, colour, 'Choose a garment colour before adding this item to your cart.');
      return false;
    }
    const product = form.closest('product-info') || form.closest('.product__info-container') || form.parentElement;
    const swatches = Array.from(product.querySelectorAll('.product-colour-swatches__radio'));
    if (swatches.length && !swatches.some(function (swatch) { return normaliseColour(swatch.value) === colourValue; })) {
      showError(calculator, colour, 'Choose one of the available garment colours shown above.');
      return false;
    }
    clearError(calculator);
    return true;
  }

  function stopInvalidSubmission(event, form) {
    if (form && /\/cart\/add\/?$/.test(new URL(form.action, location.href).pathname) && !validate(form)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  // Window capture runs before the app's document/button/form handlers. Delegation
  // also covers asynchronous calculator mounts and form replacement on variants.
  window.addEventListener('click', function (event) {
    const button = event.target.closest && event.target.closest('button[type="submit"], input[type="submit"], button[name="add"]');
    if (button && !button.disabled) stopInvalidSubmission(event, button.form);
  }, true);
  window.addEventListener('submit', function (event) { stopInvalidSubmission(event, event.target); }, true);
  document.addEventListener('input', function (event) {
    const calculator = event.target.closest && event.target.closest('#calculator');
    if (calculator) clearError(calculator);
  });
  document.addEventListener('change', function (event) {
    const calculator = event.target.closest && event.target.closest('#calculator');
    if (calculator) clearError(calculator);
  });
})();
